import type { LocationQueryValue } from 'vue-router'

interface IQueryParam<T> {
  parse(value: LocationQueryValue | LocationQueryValue[] | undefined): T
  serialize(value: T): string | undefined
}

type TQueryParams<TState> = {
  [Key in keyof TState]: IQueryParam<TState[Key]>
}

const isEmpty = (value: unknown) => value === undefined || value === null || value === ''

const logInvalidValue = (value: unknown) => {
  console.error(`[useQueryState] Invalid query value "${value}".`)
}

const toPositiveInteger = (value: unknown) => {
  if (isEmpty(value)) return undefined

  const number = Number(value)

  if (Number.isInteger(number) && number > 0) return number

  logInvalidValue(value)

  return undefined
}

export const queryParam = {
  number: (): IQueryParam<number | undefined> => ({
    parse: toPositiveInteger,
    serialize: value => value?.toString(),
  }),

  numberList: (): IQueryParam<number[]> => ({
    parse: value => String(value ?? '').split(',').map(toPositiveInteger).filter(item => item !== undefined),
    serialize: value => value.join(',') || undefined,
  }),

  string: (): IQueryParam<string | undefined> => ({
    parse: value => (typeof value === 'string' && value.trim()) || undefined,
    serialize: value => value || undefined,
  }),

  oneOf: <T>(options: Record<string, T>, defaultValue: T): IQueryParam<T> => ({
    parse: (value) => {
      const option = Object.entries(options).find(([key]) => key === value)?.[1]

      if (option === undefined && !isEmpty(value)) logInvalidValue(value)

      return option ?? defaultValue
    },
    serialize: value => Object.entries(options).find(([, option]) => option === value)?.[0],
  }),

  page: (): IQueryParam<number> => ({
    parse: value => toPositiveInteger(value) ?? 1,
    serialize: value => (value > 1 ? String(value) : undefined),
  }),
}

export const useQueryState = <TState extends Record<string, unknown>>(params: TQueryParams<TState>) => {
  const route = useRoute()
  const router = useRouter()
  const entries: [string, IQueryParam<unknown>][] = Object.entries(params)

  const readFromUrl = (): Record<string, unknown> => Object.fromEntries(
    entries.map(([key, param]) => [key, param.parse(route.query[key])]),
  )

  const toUrlQuery = (values: Record<string, unknown>) => Object.fromEntries(
    entries.map(([key, param]) => [key, param.serialize(values[key])]),
  )

  const state = reactive(readFromUrl()) as TState
  const serializedState = () => JSON.stringify(toUrlQuery(state))
  const serializedUrl = () => JSON.stringify(toUrlQuery(readFromUrl()))

  watch(serializedState, () => {
    router.replace({ query: { ...route.query, ...toUrlQuery(state) }})
  }, { flush: 'post' })

  watch(serializedUrl, () => {
    if (serializedState() !== serializedUrl()) {
      Object.assign(state, readFromUrl())
    }
  })

  return toRefs(state)
}
